import { FormControl } from "@angular/forms";
import * as t from "io-ts";
import { DateFromISOStringType, OptionFromNullableType } from "io-ts-types";
import { Option, None, Some, fromNullable } from "fp-ts/lib/Option";

import { IoFormArray } from "./io-form-array.class";
import { IoFormGroup } from "./io-form-group.class";

export class DateFromDatelike extends t.Type<Date, string | Date, unknown> {
  readonly _tag = "DateFromDatelike";
  constructor() {
    super(
      "DateFromDatelike",
      (u): u is Date => u instanceof Date,
      (u, c) => {
        if (typeof u === "string" || u instanceof Date) {
          var d = new Date(u);
          return isNaN(d.getTime()) ? t.failure(u, c) : t.success(d);
        }
        return t.failure(u, c);
      },
      (a) => a.toISOString()
    );
  }
}

export class OptionFromOptionalType<C extends t.Any, A = any, O = A, I = unknown> extends t.Type<
  A,
  O,
  I
> {
  readonly type: C;
  readonly _tag: "OptionFromOptionalType";
  constructor(
    name: string,
    is: OptionFromOptionalType<C, A, O, I>["is"],
    validate: OptionFromOptionalType<C, A, O, I>["validate"],
    serialize: OptionFromOptionalType<C, A, O, I>["encode"],
    type: C
  ) {
    super(name, is, validate, serialize);
  }
}
export interface OptionFromOptional<C extends t.Mixed>
  extends OptionFromOptionalType<C, Option<t.TypeOf<C>>, t.OutputOf<C> | null, unknown> {}
/**
 * Factory function for OptionFromOptionalType
 *
 * @since 0.1.0
 */
export const createOptionFromOptional = <C extends t.Mixed>(
  codec: C,
  _name?: string
): OptionFromOptional<C> => {
  const name = _name !== undefined ? _name : `Option<${codec.name}>`;
  const Nullable = t.union([codec, t.null, t.undefined]);

  return new OptionFromOptionalType(
    name,
    (m): m is Option<C> => m instanceof None || (m instanceof Some && codec.is(m.value)),
    (s, c) => {
      var validation = Nullable.validate(s, c);
      return validation.isLeft() ? validation : t.success(fromNullable(validation.value));
    },
    (a) => a.map(codec.encode).toUndefined(),
    codec
  );
};

/**
 * Enumeration io-ts Type
 *
 * @since 0.1.0
 */
export class EnumType<A> extends t.Type<A> {
  readonly _tag: "EnumType";
  enumObject: object;
  constructor(e: object, name?: string) {
    super(
      name || "enum",
      (u): u is A =>
        Object.keys(this.enumObject)
          .map((k) => this.enumObject[k])
          .some((v) => v === u),
      (u, c) => (this.is(u) ? t.success(u) : t.failure(u, c)),
      t.identity
    );
  }
}
/**
 * Factory for EnumType
 *
 * @since 0.1.0
 */
export const createEnumType = <T>(e: object, name?: string | undefined): EnumType<T> =>
  new EnumType(e, name);

export type Control =
  | t.StringType
  | t.NumberType
  | t.BooleanType
  | t.KeyofType<Record<string, unknown>>
  | EnumType<any>
  | DateFromISOStringType
  | DateFromDatelike;

export type Array = t.ArrayType<Control | Group>;

export type Pass =
  | OptionFromNullableType<Control | Group | Array>
  | OptionFromOptionalType<Control | Group | Array>;

export type Group = t.InterfaceType<{
  [key: string]: Control | Group | Array | Pass;
}>;

export type IO = Control | Array | Pass | Group;
export type Container = Group | Array;

const UNKNOWN_TYPE_WARNING =
  "IoFormService encountered an unknown type codec, defaulting to FormControl for type";

export const ioToForm = (io: IO): IoFormGroup | IoFormArray | FormControl => {
  switch (io._tag) {
    // FormControls
    case "BooleanType":
    case "NumberType":
    case "StringType":
    case "EnumType":
    case "DateFromISOStringType":
    case "DateFromDatelike":
    case "KeyofType":
      return new FormControl();

    // IoFormGroups
    case "InterfaceType":
      let group = {};
      for (let k in io.props) {
        group[k] = ioToForm(io.props[k]);
      }
      return new IoFormGroup(group);

    // FormArrays
    case "ArrayType":
      const generateControl = () => ioToForm(io.type);
      return new IoFormArray([], undefined, undefined, generateControl);

    // Special Case: Unwrap Options
    case "OptionFromOptionalType":
    case "OptionFromNullableType":
      // Unwrap Option types
      return ioToForm(io.type);

    // Default to FormControl for unknown types
    default:
      // Warn and default to FormControl for things we don't understand.
      console.warn(UNKNOWN_TYPE_WARNING, io);
      return new FormControl();
  }
};
